import { useQuery } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type { StatsParams, TimelineParams } from '../types'

/**
 * Query key factory for stats-related queries
 */
export const statsKeys = {
  all: ['stats'] as const,
  get: (params?: StatsParams) => [...statsKeys.all, 'get', params] as const,
  timeline: (params?: TimelineParams) => [...statsKeys.all, 'timeline', params] as const,
  quick: () => [...statsKeys.all, 'quick'] as const,
  analytics: () => [...statsKeys.all, 'analytics'] as const,
}

/**
 * Hook to fetch dashboard statistics
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useStats({ startDate: '2024-01-01' })
 * console.log(data?.runs.total)
 * ```
 */
export function useStats(params?: StatsParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: statsKeys.get(params),
    queryFn: () => client.stats.get(params),
  })
}

/**
 * Hook to fetch timeline data for charts
 *
 * @example
 * ```tsx
 * const { data } = useStatsTimeline({ period: '7d' })
 * ```
 */
export function useStatsTimeline(params?: TimelineParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: statsKeys.timeline(params),
    queryFn: () => client.stats.timeline(params),
  })
}

/**
 * Hook to fetch quick stats (local DB only - fast < 100ms)
 * Use this for initial dashboard load
 *
 * @example
 * ```tsx
 * const { data } = useStatsQuick()
 * console.log(data?.errors.total)
 * ```
 */
export function useStatsQuick() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: statsKeys.quick(),
    queryFn: () => client.stats.quick(),
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to fetch analytics stats (pre-computed from automation-core)
 * Data is computed by a background job every 5 minutes
 *
 * @example
 * ```tsx
 * const { data } = useStatsAnalytics()
 * if (data?.status === 'ready') {
 *   console.log(data.data?.totalRuns)
 * }
 * ```
 */
export function useStatsAnalytics() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: statsKeys.analytics(),
    queryFn: () => client.stats.analytics(),
    staleTime: 300_000, // 5 minutes
    // Poll if analytics are still pending
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? 5000 : false,
  })
}
